/**
 * useExport hook
 * Performs authenticated file downloads using fetch + blob URL.
 * The Authorization header cannot be set on <a href> links,
 * so we fetch the file with the JWT and trigger a programmatic download.
 */

import { useState } from 'react';
import Cookies from 'js-cookie';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5203';

export const useExport = () => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const download = async (path, filename) => {
    setDownloading(true);
    setError(null);
    try {
      const token = Cookies.get('userToken');
      const res = await fetch(`${BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Export failed (${res.status})`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'export';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Open a report in a new browser tab (for HTML reports with print dialog).
   */
  const openReport = async (path) => {
    setDownloading(true);
    setError(null);
    try {
      const token = Cookies.get('userToken');
      const res = await fetch(`${BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Report failed (${res.status})`);

      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after short delay to let the new tab load
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.message || 'Report generation failed');
    } finally {
      setDownloading(false);
    }
  };

  return { download, openReport, downloading, error };
};

export default useExport;
